/**
 * Utility functions for proposal form logic
 */

/**
 * Determines the correct insertType (Fact Sheet or Snapshot) based on program name
 * @param {string} programName - The name of the proposal program
 * @returns {string} "Fact Sheet", "Snapshot", or "" if no program provided
 */
export const getInsertTypeForProgram = (programName) => {
  if (!programName) return "";
  const lowerProgram = programName.toLowerCase();

  // Elements always uses Snapshot
  if (lowerProgram.includes("elements")) {
    return "Snapshot";
  }

  // CoreOps (non-Focused) and Global alphaTrend always use Fact Sheet
  if (
    (lowerProgram.includes("coreops") && !lowerProgram.includes("focused")) ||
    lowerProgram.includes("global alphatrend")
  ) {
    return "Fact Sheet";
  }

  // Default to Fact Sheet for all other programs
  return "Fact Sheet";
};

/**
 * Checks if a program should have its insertType radio buttons disabled
 * Programs with only one valid insertType option should have radio buttons disabled
 * @param {string} programName - The name of the proposal program
 * @returns {boolean} true if radio buttons should be disabled
 */
export const shouldDisableInsertTypeRadios = (programName) => {
  if (!programName) return true;
  const lowerProgram = programName.toLowerCase();

  // These programs have only one valid option and should have radios disabled
  return (
    lowerProgram.includes("elements") ||
    (lowerProgram.includes("coreops") && !lowerProgram.includes("focused")) ||
    lowerProgram.includes("global alphatrend")
  );
};

/**
 * Checks if a program is a Fusion program
 * @param {string} programName - The name of the proposal program
 * @returns {boolean} true if program is a fusion program
 */
export const isFusionProgram = (programName) => {
  if (!programName) return false;
  return programName.toLowerCase().includes("fusion");
};

/**
 * Returns the user-facing program name while preserving the raw program key
 * for lookups and saved payloads.
 * @param {string} programName
 * @param {Array<Object>} proposalTypes
 * @returns {string}
 */
export const getProgramDisplayName = (programName, proposalTypes = []) => {
  if (!programName) return "";
  const match = Array.isArray(proposalTypes)
    ? proposalTypes.find(
        (pt) =>
          pt &&
          String(pt.name).toLowerCase() === String(programName).toLowerCase()
      )
    : null;
  const configuredDisplay = match?.displayName || match?.label;
  if (configuredDisplay) return configuredDisplay;
  if (String(programName).toLowerCase() === "fusion") {
    return "Fusion Managed Platform";
  }
  return programName;
};

/**
 * Returns the first selected registration value from the form's registrations structure.
 * @param {Array|string} registrations
 * @returns {string}
 */
export const getPrimaryRegistrationValue = (registrations) => {
  if (Array.isArray(registrations)) {
    const firstRegistration = registrations[0];
    if (typeof firstRegistration === "string") {
      return firstRegistration;
    }
    return firstRegistration?.value || "";
  }

  if (typeof registrations === "string") {
    return registrations;
  }

  return "";
};

/**
 * Formats the ISP account-goal field for proposal handoff/reference text.
 * Handles the legacy single-select value and the MMMS checkbox array.
 * @param {string|string[]} accountGoal
 * @param {string} accountGoalOther
 * @returns {string}
 */
export const formatIspAccountGoalForDisplay = (
  accountGoal,
  accountGoalOther = ""
) => {
  const formatOne = (goal) => {
    if (!goal) return "";
    if (goal === "Funding for another goal") {
      return accountGoalOther || "Funding for another goal";
    }
    if (goal === "Other") {
      return accountGoalOther ? `Other: ${accountGoalOther}` : "Other";
    }
    return goal;
  };

  if (Array.isArray(accountGoal)) {
    return accountGoal
      .map(formatOne)
      .filter(Boolean)
      .join(", ");
  }

  return formatOne(accountGoal);
};

/**
 * Checks if a registration should force taxable bond selection.
 * @param {string} registrationValue
 * @param {Array} registrationTypes
 * @returns {boolean}
 */
export const shouldForceTaxableForRegistration = (
  registrationValue,
  registrationTypes = []
) => {
  if (!registrationValue || !Array.isArray(registrationTypes)) return false;
  const registrationType = registrationTypes.find(
    (reg) => reg.name === registrationValue
  );
  return registrationType?.["Account Type"] === "Qualified";
};

const getFallbackBondOptions = (program) => {
  if (!program || typeof program !== "object") return [];

  for (const key of Object.keys(program)) {
    if (key.endsWith("Bonds") && Array.isArray(program[key])) {
      return program[key];
    }
  }

  return [];
};

/**
 * Gets the applicable risk tolerance options for a program and bond side.
 * @param {Object|string} program
 * @param {string} bondType
 * @param {string} registrationValue
 * @param {Array} registrationTypes
 * @returns {string[]}
 */
export const getRiskToleranceOptionsForProgram = (
  program,
  bondType = "Taxable",
  registrationValue = "",
  registrationTypes = []
) => {
  if (!program || typeof program !== "object") return [];

  const forceTaxable = shouldForceTaxableForRegistration(
    registrationValue,
    registrationTypes
  );
  const effectiveBondType = forceTaxable ? "Taxable" : bondType || "Taxable";

  if (Array.isArray(program.taxableBonds) || Array.isArray(program.municipalBonds)) {
    if (effectiveBondType === "Municipal" && Array.isArray(program.municipalBonds)) {
      return program.municipalBonds;
    }
    if (Array.isArray(program.taxableBonds)) {
      return program.taxableBonds;
    }
    return Array.isArray(program.municipalBonds) ? program.municipalBonds : [];
  }

  return getFallbackBondOptions(program);
};

/**
 * Determines whether the bond-type selector should be shown for a program.
 * @param {Object|string} program
 * @param {string} registrationValue
 * @param {Array} registrationTypes
 * @returns {boolean}
 */
export const shouldShowBondTypeOptions = (
  program,
  registrationValue = "",
  registrationTypes = []
) => {
  if (!program || typeof program !== "object") return false;
  if (
    shouldForceTaxableForRegistration(registrationValue, registrationTypes)
  ) {
    return false;
  }
  return (
    Array.isArray(program.taxableBonds) &&
    program.taxableBonds.length > 0 &&
    Array.isArray(program.municipalBonds) &&
    program.municipalBonds.length > 0
  );
};

/**
 * Determines whether a proposal type is ISP-only and should be hidden from
 * proposal generation (cards, strategy dropdowns, Fusion sub-strategies).
 * Handled analogously to the `enabled` flag; accepts boolean true or "true".
 * @param {Object} proposalType
 * @returns {boolean}
 */
export const isIspOnlyStrategy = (proposalType) =>
  !!proposalType &&
  (proposalType.isponly === true || proposalType.isponly === "true");

/**
 * Resolves whether a submission row's program name maps to an ISP-only
 * strategy. Used to block proposal creation from ISP-only submissions.
 * @param {string} programName - the submission's `program` value
 * @param {Array<Object>} proposalTypes - full proposal-types list
 * @returns {boolean}
 */
export const isIspOnlyProgram = (programName, proposalTypes) => {
  if (!programName || !Array.isArray(proposalTypes)) return false;
  const match = proposalTypes.find(
    (pt) =>
      pt &&
      String(pt.name).toLowerCase() === String(programName).toLowerCase()
  );
  return isIspOnlyStrategy(match);
};

/**
 * Returns true if any of a submission's Fusion sub-strategy allocations maps to
 * an ISP-only strategy. Used to block proposal creation from Fusion ISPs that
 * include an ISP-only sleeve.
 *
 * Submission-table rows persist the ISP payload as a JSON string in
 * `ispFormData`, with allocations under `fusionAllocations`. The pre-parsed
 * `ispFusionAllocations` shape only exists on the proposal-creation navigation
 * payload, so we accept either: prefer the parsed array, otherwise parse
 * `ispFormData` and read `fusionAllocations`.
 * @param {Object} proposal - submission row (`ispFormData` JSON string) or
 *   navigation payload (`ispFusionAllocations` array)
 * @param {Array<Object>} proposalTypes - full proposal-types list
 * @returns {boolean}
 */
export const hasIspOnlyFusionSubStrategy = (proposal, proposalTypes) => {
  let allocations = proposal?.ispFusionAllocations;
  if (!Array.isArray(allocations) && typeof proposal?.ispFormData === "string") {
    try {
      allocations = JSON.parse(proposal.ispFormData)?.fusionAllocations;
    } catch (e) {
      allocations = undefined;
    }
  }
  return (
    Array.isArray(allocations) &&
    allocations.some((allocation) =>
      isIspOnlyProgram(allocation?.strategy, proposalTypes)
    )
  );
};

const normalizeAdvisorName = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Returns true if the strategy is available given the advisor name(s) selected
 * on the form. The strategy's optional `onlyForAdvisors` attribute is a list
 * of names; if missing or empty the strategy is universal. If populated, the
 * strategy is available when at least one candidate name matches one entry on
 * the list. Matching is case-insensitive after stripping characters outside
 * [a-z ] from both sides.
 *
 * @param {Object} proposalType
 * @param {string|string[]} advisorNames - primary + any additional advisors
 * @returns {boolean}
 */
export const isStrategyAvailableForAdvisors = (proposalType, advisorNames) => {
  if (!proposalType) return true;
  const list = proposalType.onlyForAdvisors;
  if (!Array.isArray(list)) return true;
  const normalizedAllowed = list.map(normalizeAdvisorName).filter(Boolean);
  if (normalizedAllowed.length === 0) return true;
  const candidates = (Array.isArray(advisorNames) ? advisorNames : [advisorNames])
    .map(normalizeAdvisorName)
    .filter(Boolean);
  if (candidates.length === 0) return false;
  return candidates.some((c) => normalizedAllowed.includes(c));
};

/**
 * Determines whether a proposal type should be available in Fusion strategy
 * selection. Normal strategies gate on riskScore. ISP-only strategies are
 * included only when allowIspOnly is true AND the strategy defines fusionLimits.
 * @param {Object} proposalType
 * @param {Object} [options]
 * @param {boolean} [options.allowIspOnly=false] - Set true in ISP context only
 * @returns {boolean}
 */
export const isFusionEligibleStrategy = (
  proposalType,
  { allowIspOnly = false } = {}
) => {
  if (!proposalType?.name) return false;
  if (isFusionProgram(proposalType.name)) return false;
  if (isIspOnlyStrategy(proposalType)) {
    // ISP-only strategies are eligible only in ISP context (allowIspOnly) and
    // only when they define fusionLimits. Never eligible in proposal generation.
    return allowIspOnly && !!proposalType.fusionLimits;
  }
  return !!proposalType.riskScore;
};

/**
 * Determines whether a strategy supports the requested bond side.
 * Strategies without explicit taxable/municipal splits are treated as compatible with either.
 * @param {Object} proposalType
 * @param {string} bondType
 * @returns {boolean}
 */
export const isStrategyCompatibleWithBondType = (
  proposalType,
  bondType = ""
) => {
  if (!proposalType || !bondType) return true;

  const hasTaxable = Array.isArray(proposalType.taxableBonds);
  const hasMunicipal = Array.isArray(proposalType.municipalBonds);

  if (!hasTaxable && !hasMunicipal) {
    return true;
  }

  if (bondType === "Municipal") {
    return hasMunicipal && proposalType.municipalBonds.length > 0;
  }

  return hasTaxable && proposalType.taxableBonds.length > 0;
};

/**
 * Gets the proposal types eligible for Fusion strategy selection.
 * @param {Array} proposalTypes
 * @param {Object} options
 * @param {string} options.bondType
 * @param {boolean} [options.allowIspOnly=false] - When true, ISP-only strategies with fusionLimits are included
 * @returns {Array}
 */
export const getEligibleFusionStrategies = (
  proposalTypes = [],
  { bondType = "", allowIspOnly = false } = {}
) => {
  return proposalTypes
    .filter((proposalType) => isFusionEligibleStrategy(proposalType, { allowIspOnly }))
    .filter((proposalType) =>
      isStrategyCompatibleWithBondType(proposalType, bondType)
    );
};

const normalizeFusionRiskText = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/**
 * Gets the configured numeric risk score for one Fusion allocation model.
 * Mirrors the OPG Fusion lookup: exact normalized key match first, then a
 * partial normalized match for labels that include extra tax/model wording.
 * @param {Object} proposalType
 * @param {string} riskTolerance
 * @returns {{key: string, value: number}|null}
 */
export const getFusionAllocationRiskScore = (proposalType, riskTolerance) => {
  if (!proposalType || !riskTolerance) return null;
  const riskScore = proposalType.riskScore;
  if (riskScore === undefined || riskScore === null) return null;

  if (typeof riskScore === "number" || typeof riskScore === "string") {
    const value = Number(riskScore);
    return Number.isFinite(value) ? { key: riskTolerance, value } : null;
  }

  if (typeof riskScore !== "object") return null;

  const normalizedRiskTolerance = normalizeFusionRiskText(riskTolerance);
  if (!normalizedRiskTolerance) return null;
  for (const [riskKey, riskValue] of Object.entries(riskScore)) {
    const normalizedRiskKey = normalizeFusionRiskText(riskKey);
    if (normalizedRiskKey === normalizedRiskTolerance) {
      const value = Number(riskValue);
      return Number.isFinite(value) ? { key: riskKey, value } : null;
    }
  }

  for (const [riskKey, riskValue] of Object.entries(riskScore)) {
    const normalizedRiskKey = normalizeFusionRiskText(riskKey);
    if (
      normalizedRiskKey.includes(normalizedRiskTolerance) ||
      normalizedRiskTolerance.includes(normalizedRiskKey)
    ) {
      const value = Number(riskValue);
      return Number.isFinite(value) ? { key: riskKey, value } : null;
    }
  }

  return null;
};

/**
 * Gets the risk tolerance category from a numeric score. Math.trunc matches
 * the OPG Fusion behavior, which ignores decimals for category boundaries.
 * @param {number|string} score
 * @returns {string}
 */
export const getRiskToleranceCategoryFromScore = (score) => {
  const value = Number(score);
  if (!Number.isFinite(value)) return "";
  const wholeScore = Math.trunc(value);
  if (wholeScore <= 35) return "Conservative";
  if (wholeScore <= 65) return "Moderate";
  return "Growth";
};

/**
 * Calculates the weighted Fusion allocation risk score for ISP Section 4.
 * ISP sleeves use percentage weights; weighting by percentage is equivalent
 * to weighting each sleeve by its portion of the Initial Investment Amount.
 * @param {Array<Object>} allocations
 * @param {Array<Object>} proposalTypes
 * @returns {{score: number|null, category: string, totalWeight: number, hasMissingScore: boolean}}
 */
export const calculateFusionWeightedRiskScore = (
  allocations = [],
  proposalTypes = []
) => {
  const proposalTypeByName = new Map(
    (Array.isArray(proposalTypes) ? proposalTypes : [])
      .filter((proposalType) => proposalType?.name)
      .map((proposalType) => [proposalType.name, proposalType])
  );
  let totalWeight = 0;
  let weightedScore = 0;
  let scoredAllocations = 0;
  let hasMissingScore = false;

  (Array.isArray(allocations) ? allocations : []).forEach((allocation) => {
    const weight = Number(allocation?.percentage);
    if (!allocation?.strategy || !allocation?.riskTolerance || !weight || weight <= 0) {
      return;
    }
    totalWeight += weight;
    const proposalType = proposalTypeByName.get(allocation.strategy);
    const riskScore = getFusionAllocationRiskScore(
      proposalType,
      allocation.riskTolerance
    );
    if (!riskScore) {
      hasMissingScore = true;
      return;
    }
    weightedScore += riskScore.value * (weight / 100);
    scoredAllocations += 1;
  });

  if (scoredAllocations === 0) {
    return {
      score: null,
      category: "",
      totalWeight,
      hasMissingScore,
    };
  }

  return {
    score: weightedScore,
    category: getRiskToleranceCategoryFromScore(weightedScore),
    totalWeight,
    hasMissingScore,
  };
};

export const getFusionAllocationDisplayName = (
  allocation = {},
  proposalTypes = []
) => {
  const strategy = allocation?.strategy || "";
  const strategyDisplay = getProgramDisplayName(strategy, proposalTypes);
  let riskTolerance = allocation?.riskTolerance || "";
  const taxSensitivity = allocation?.taxSensitivity || "";
  const strategyWords = strategyDisplay.trim().split(/\s+/);
  const riskWords = riskTolerance.trim().split(/\s+/);
  if (
    strategyWords.length > 0 &&
    riskWords.length > 1 &&
    strategyWords[strategyWords.length - 1].toLowerCase() ===
      riskWords[0].toLowerCase()
  ) {
    riskTolerance = riskWords.slice(1).join(" ");
  }
  const combined = `${strategyDisplay} ${riskTolerance}`.toLowerCase();
  const parts = [strategyDisplay];

  if (riskTolerance && !strategyDisplay.toLowerCase().includes(riskTolerance.toLowerCase())) {
    parts.push(riskTolerance);
  }
  if (taxSensitivity && !combined.includes(taxSensitivity.toLowerCase())) {
    parts.push(taxSensitivity);
  }

  return parts.filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
};
